import { useEffect, useMemo, useRef, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";

import { ContactQuickLinks } from "@/components/ContactQuickLinks";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField, RecordFieldProvenance } from "@/components/record/fields";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { DocumentsIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  CoTenantResource,
  LeaseResource,
  Page,
  RentReviewResource,
  RenewalResource,
  TenantResource,
} from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { LEASE_STATUS_LABELS } from "@/lib/enum-labels";
import { formatDateCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

/**
 * `/tenants-leases/leases/:id` (Story 6.5) — this codebase's first lease
 * record page (Story 6.3 was backend-only; its own Code Map said so
 * explicitly). Built the same way `PropertyRecord.tsx`/`TenantRecord.tsx`
 * were: `RecordPage`/`FieldRow` for the lease's own §4.3 fields (mechanical
 * — every field `LeaseResource` carries, mirroring `PropertyRecord.tsx`'s
 * own field-list style), plus a co-tenants section listing each attached
 * tenant with real one-click contact actions (VC-6.5-07).
 *
 * Routing (a real judgement call, not specified by the frozen spec's own
 * five named ones): wired at `/tenants-leases/leases/:id`, a sibling path
 * under the same `/tenants-leases` nav destination — not the bare
 * `/tenants-leases/:id` a tenant's own record page already occupies. Two
 * structurally different resources (`GET /tenants/{id}` vs. `GET
 * /leases/{id}`) sharing one ambiguous path segment would force a "guess
 * which one this id names" dispatcher — a fragile double-fetch this
 * codebase has never needed anywhere else, and a real regression risk for
 * every existing tenant-record bookmark/link. Every other record page here
 * maps one route deterministically to one resource and one query hook
 * (`PropertyRecord` ⇄ `/properties/:id`, `MunicipalityRecord` ⇄
 * `/municipalities/:id`, `UserRecord` ⇄ `/settings/users/:id`); a nested
 * sub-path per entity type under one shared nav destination is this
 * codebase's own established shape for exactly this situation (mirrors
 * `/settings/users/:id` nested under `/settings`). No new lease *list*
 * screen was added by *this* story: no AC in Story 6.5 asked for one, and a
 * lease was genuinely reachable at the time from the tenant record's own
 * real, working lease-history section alone — building a full list table
 * ahead of a named requirement was exactly the kind of scope this codebase
 * has repeatedly chosen not to build (mirrors `PropertyRecord.tsx`'s own
 * disposition-field judgement call 6). Story 6.7 is the story that finally
 * names the requirement (the AC's own "Rent roll" saved view presupposes a
 * real list) — see `LeasesList.tsx`, wired at the sibling path
 * `/tenants-leases/leases`.
 *
 * **Story 15.15** builds this codebase's first generic `PATCH /leases/{id}`
 * route and wires real inline editing here, finally bringing this page to
 * parity with `PropertyRecord.tsx`/`TenantRecord.tsx` — every §4.3 field
 * from `POST /leases`/`CreateLeaseRequest` gets an `edit` config *except*:
 * `status` (stays governed exclusively by its own dedicated `PATCH
 * /leases/{id}/status` transition route, AD-23 — `UpdateLeaseRequest` has no
 * `status` field at all, so there is no way to send it through this page's
 * own inline editing even by mistake), `unit_id`/`id` (Judgement call 2:
 * reassigning a lease to a different unit is a larger, out-of-scope
 * operation), and three structured list-shaped fields —
 * `utilities_included`, `scheduled_escalation`, `lease_documents` — left
 * read-only for the identical, already-established reason
 * `TenantRecord.tsx` leaves `additional_occupants`/`pets`/`vehicles`
 * read-only: a single free-text `InlineEditCell` cannot represent a list of
 * structured objects (or a list of strings where an individual entry might
 * itself need a delimiter) without the same lossy-round-trip risk
 * `PropertyRecord.tsx`'s own `DispositionClosingDocumentsField` was built to
 * avoid for `disposition_closing_documents` — building three more bespoke
 * multi-row editors is a real scope expansion no AC in this story names,
 * consistent with `TenantRecord.tsx`'s own precedent of leaving its
 * structurally identical fields for a later, dedicated pass instead.
 *
 * `notify_by`/`renewed_into_lease_id` also stay read-only — both are
 * resolved/derived values, never real editable columns on this row (the
 * first is computed fresh by the backend on every read; the second is set
 * only by the renewal action, Story 6.7).
 *
 * This page has no `onResolveConflict` wiring, deliberately mirroring
 * `PropertyRecord.tsx`/`TenantRecord.tsx` exactly. The actual, consistent
 * rule (confirmed by direct code search before writing this — `UserRecord.
 * tsx` is a genuine single-record detail page built on this same
 * `RecordPage` shell, and it DOES wire `onResolveConflict`, so "record page
 * vs. list" is not what distinguishes them): conflict UI exists only where
 * the entity's own `PATCH` route implements real optimistic concurrency
 * (an `expected_updated_at` field in the request, a `conflict`-coded 409,
 * "Keep mine"/"Keep theirs") — `PATCH /users/{id}` has that; `PATCH
 * /tenants/{id}` and `PATCH /properties/{id}` do not, and neither does this
 * story's new `PATCH /leases/{id}`. A failed `PATCH /leases/{id}` here
 * renders through the same generic `ApiError`-message path `sendPatch`
 * already uses for every
 * other record page, exactly like `PropertyRecord.tsx`/`TenantRecord.tsx`.
 *
 * Story 6.6's "Notify-by date" (`notify_by`, right after `end_date`, in the
 * fields-that-matter group — the product's own headline feature, per this
 * story's own Intent): `LeaseResource.notify_by` is already the resolved
 * `{date, tier, confirmed_on, usable}` shape `GET /leases/{id}` computes
 * fresh on every read (`app.domain.tenancy.notify_by.compute_notify_by`),
 * so this page does no resolving of its own — only rendering, reusing
 * `FieldRow`'s existing UX-DR8 provenance marker exactly the way the twelve
 * resolved municipal settings on `PropertyRecord.tsx` already do. A `null`
 * `notify_by.date` (a lease with no `end_date` on file, a real, legal state)
 * renders an honest sentence instead of a date, with no provenance marker
 * at all — there is no date being claimed, so nothing to flag as an
 * unconfirmed default (VC-6.6-07).
 *
 * **Story 11.6 (R-L8)** adds one more small, inline section, near the
 * existing rent field (spec judgement call 4): a real, working `GET
 * /rent-reviews?lease_id=...` fetch, rendered only when the response
 * contains an *open* review (`reviewed_on === null`) — a lease with none
 * due yet, or not an eligible type/status at all, shows nothing extra (AC's
 * own "no empty rent-review section cluttering every other lease's record
 * page"). Recording the outcome (`POST /rent-reviews/{id}/record`) is
 * Owner-only; the rent-control warning renders inline, computed from
 * `current_rent_cents`/`rent_control_cap_percent` (both already resolved,
 * never-stored fields on `RentReviewResource`, AD-10) against whatever the
 * Owner is currently typing — a live, pre-submit preview mirroring the same
 * percentage math `app.domain.rent_control.check_rent_control_cap` computes
 * authoritatively server-side, so the warning is visible before the write,
 * not only after it.
 *
 * **Story 15.22** adds this page's first caller of `PATCH /leases/{id}/
 * status` (AD-23, `transitions.transition`) — a lease created through "Add
 * lease" starts, and previously stayed forever, `Draft`; nothing anywhere
 * in the frontend ever moved it out. Two real actions, "Sign & start" and
 * "Cancel lease", render only while `status === "draft"` (`Draft`'s only two
 * legal events, `transitions.py`'s own `_TRANSITIONS` table), each behind a
 * `ConfirmDialog` before firing (mirrors `PropertyRecord.tsx`'s own
 * `Archive` confirmation, spec judgement call 1). Success writes the
 * server's own returned record straight into this page's cache via the same
 * `writeRecordToCache` Story 15.15's inline edits already use, so the
 * displayed status updates with no manual reload; a rejection (e.g.
 * `transitions.ConcurrentLeaseStatusTransitionError` — someone else already
 * moved the lease out of `Draft` first) shows the server's own honest
 * message inside the still-open dialog and refetches this page's own record
 * so the background is never left showing the stale pre-click status
 * (VC-15.22-04). No new "must have a tenant first" precondition is added
 * (Judgement call 2) — a zero-tenant `Draft` lease can be signed & started
 * here exactly as the backend already allows.
 */

//: Story 15.15 — mirrors `PropertyRecord.tsx`'s/`TenantRecord.tsx`'s
//: identical `YES_NO_OPTIONS` constant exactly, for this page's two
//: boolean-shaped fields (`auto_renew`, `deposit_interest_bearing`).
const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

//: Story 15.15 — the value half of `PATCH /leases/{id}`'s body a commit
//: produces, mirroring `PropertyRecord.tsx`'s/`TenantRecord.tsx`'s
//: identical `patchBodyFor`: a cleared value returns the field to
//: "unanswered" (`null`), not to an empty string a date/numeric column
//: would refuse. Every field this page makes editable round-trips through
//: Pydantic's own lax coercion the same way `PropertyRecord.tsx`'s numeric/
//: boolean `...textEdit`/`...yesNoEdit` fields already do (`year_built`,
//: `built_before_1978`, etc.) — no per-field special-casing is needed here
//: the way `TenantRecord.tsx`'s own `id_verified` needed one, since every
//: editable field on this page follows that same, already-proven shape.
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  return { [fieldKey]: payload.value === "" ? null : payload.value };
}

const NO_END_DATE_MESSAGE = "No end date on file — there is no notify-by date to compute yet.";

function formatNotifyBy(notifyBy: LeaseResource["notify_by"]): string {
  return notifyBy.date ? formatDateCell(notifyBy.date) : NO_END_DATE_MESSAGE;
}

/** Judgement call 1's own "lease-explicit" tier (`app.domain.tenancy.
 * notify_by.NotifyByTier`, a deliberate sibling of `settings_resolver.Tier`,
 * never folded into it) maps onto `RecordFieldProvenance`'s own existing
 * `"confirmed"` tier — reserved, until this story, for exactly this case
 * (`PropertyRecord.tsx`'s own doc comment: "nothing here ever uses it,
 * since every field this route passes a `provenance` prop for is one of the
 * twelve"). `usable=true` is always set for this tier by the backend, so
 * `needsProvenanceWarning` never marks it regardless of `tier`/`confirmedOn`
 * (VC-6.6-06). A `null` `date` renders no provenance at all (see this file's
 * own top-of-file doc comment) — there is nothing to warn about when no date
 * is shown in the first place. */
function notifyByProvenance(notifyBy: LeaseResource["notify_by"]): RecordFieldProvenance | undefined {
  if (notifyBy.date === null) {
    return undefined;
  }
  return {
    tier: notifyBy.tier === "lease_explicit" ? "confirmed" : (notifyBy.tier as RecordFieldProvenance["tier"]),
    confirmedOn: notifyBy.confirmed_on,
    usable: notifyBy.usable,
  };
}

function formatCents(cents: number | null): string {
  if (cents === null) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function formatYesNo(value: boolean | null): string {
  if (value === null) {
    return "—";
  }
  return value ? "Yes" : "No";
}

function formatStringList(value: string[] | null): string {
  return value && value.length > 0 ? value.join(", ") : "—";
}

//: Story 11.6's own live, pre-submit rent-control preview (spec judgement
//: call 4) — a client-side echo of `app.domain.rent_control.check_rent_
//: control_cap`'s own percentage math, computed against whatever the Owner
//: is currently typing, before the write. The server's own response after
//: `POST /rent-reviews/{id}/record` (`RentReviewResource.rent_control`) is
//: still the one authoritative source once a value is actually committed —
//: this is only ever shown before that request happens.
function previewRentControlIncrease(
  currentRentCents: number | null,
  proposedRentCents: number,
  capPercent: string | null,
): { increasePercent: number; exceeds: boolean; capPercent: number } | null {
  if (currentRentCents === null || currentRentCents <= 0 || capPercent === null) {
    return null;
  }
  const cap = Number(capPercent);
  if (Number.isNaN(cap)) {
    return null;
  }
  const increasePercent = ((proposedRentCents - currentRentCents) / currentRentCents) * 100;
  return { increasePercent, exceeds: increasePercent > cap, capPercent: cap };
}

function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

//: Story 15.23 — after either of this page's two mutation paths that can
//: change a lease's own dates or in-force status succeeds (this file's
//: inline `PATCH /leases/{id}` edit, Story 15.15, and this file's own
//: `PATCH /leases/{id}/status` transition, Story 15.22), the Tenant,
//: Property, and Leases-list query families all get invalidated —
//: alongside, never instead of, this page's own existing `writeRecordToCache`
//: optimistic write. Three broad top-level prefixes (story's own judgement
//: call 2: broad-and-correct beats narrow-and-risky), not a client-side
//: re-derivation of either bulk resolver's own selection rule (AD-2's own
//: "one place" rule for exactly that class of logic). `invalidateQueries`
//: only marks currently-mounted observers stale and lets a future mount
//: refetch — it issues no request at all when nothing is mounted, so this
//: adds no cost to a normal page load (VC-15.23-04).
function invalidateLeaseDerivedViews(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["leases"] }),
    queryClient.invalidateQueries({ queryKey: ["tenants"] }),
    queryClient.invalidateQueries({ queryKey: ["properties"] }),
  ]);
}

function formatScheduledEscalation(value: LeaseResource["scheduled_escalation"]): string {
  if (!value || value.length === 0) {
    return "—";
  }
  return value
    .map((entry) => {
      const effectiveDate = typeof entry.effective_date === "string" ? entry.effective_date : "—";
      const rent = typeof entry.monthly_rent_cents === "number" ? formatCents(entry.monthly_rent_cents) : "—";
      return `${effectiveDate}: ${rent}`;
    })
    .join(", ");
}

export function LeaseRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isStartingRenewal, setIsStartingRenewal] = useState(false);
  //: Story 15.22 — which of `Draft`'s two legal exits (`transitions.py`'s
  //: own `_TRANSITIONS` table) the confirm dialog below is currently open
  //: for, or `null` when neither is. Mirrors `removeTarget`'s identical
  //: "the open value doubles as the dialog's own open flag" shape just
  //: below in this same file.
  const [statusAction, setStatusAction] = useState<"sign_and_start" | "cancel" | null>(null);
  const [isTransitioningStatus, setIsTransitioningStatus] = useState(false);
  const [statusTransitionError, setStatusTransitionError] = useState<string | undefined>(undefined);
  //: `POST /leases/{lease_id}/tenants`/`DELETE /leases/{lease_id}/tenants/
  //: {tenant_id}` are both `role=Role.OWNER` (spec AC: "attach/remove
  //: remain role=Role.OWNER, matching every other lease-mutation route") —
  //: the attach/remove affordances below are withheld from anyone but a
  //: confirmed Owner, not merely "not Read-only" (a Member is not
  //: Read-only and would otherwise see these, then 403 the moment they
  //: submitted — the same Tier A fix `PropertyRecord.tsx`/`TenantRecord.
  //: tsx` both needed for the identical reason).
  const isOwner = useIsOwner();

  const queryKey = useMemo(() => ["leases", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<LeaseResource>(`/leases/${id}`),
    enabled: id !== undefined,
  });

  //: The tenant roster this page's own "Add co-tenant" picker offers —
  //: fetched only for an Owner (the only actor who can ever attach one),
  //: mirroring `PropertyRecord.tsx`'s own `propertyTypesQuery` shape for a
  //: small, wholesale-fetched option list.
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list", "for-lease-record"] as const,
    queryFn: () => apiFetch<Page<TenantResource>>("/tenants"),
    enabled: isOwner,
  });

  useEffect(() => {
    document.title = recordQuery.data
      ? `Lease starting ${formatDateCell(recordQuery.data.start_date)} — PropDesk`
      : "Lease — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: LeaseResource) {
    queryClient.setQueryData<LeaseResource>(queryKey, updated);
  }

  //: Story 15.15 — mirrors `PropertyRecord.tsx`'s/`TenantRecord.tsx`'s
  //: identical `sendPatch`/`commitEdit` shape exactly: a real `PATCH
  //: /leases/{id}`, an optimistic cache write on success, and the generic
  //: `ApiError`-message path on failure (this page's own top-of-file doc
  //: comment explains why there is no `conflict`-specific branch here).
  async function sendPatch(
    row: LeaseResource,
    field: DataTableColumn<LeaseResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<LeaseResource>(`/leases/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(field.key, payload)),
      });
      writeRecordToCache(updated);
      await invalidateLeaseDerivedViews(queryClient);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<LeaseResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  //: Story 11.1's one entry point onto the renewal record (spec judgement
  //: call 5: no "Renewals" list screen exists yet). `term` is omitted from
  //: the request body — `POST /renewals` defaults it to the day after this
  //: lease's own `end_date` (the AC's own default), so this button needs no
  //: form of its own; the new record's `/renewals/:id` page is where every
  //: field actually gets filled in.
  async function handleStartRenewal(leaseId: string) {
    setIsStartingRenewal(true);
    try {
      const created = await apiFetch<RenewalResource>("/renewals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lease_id: leaseId }),
      });
      navigate(`/renewals/${created.id}`);
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message);
        return;
      }
      throw error;
    } finally {
      setIsStartingRenewal(false);
    }
  }

  //: Story 15.22 — the first frontend caller of `PATCH /leases/{id}/status`
  //: (AD-23: `transitions.transition()` stays the one writer of
  //: `Lease.status`; this is a caller of that existing mechanism, never a
  //: second way to reach it). `Draft`'s only two legal events
  //: (`transitions.py`'s own `_TRANSITIONS` table) are `sign_and_start` (->
  //: `Active`) and `cancel` (-> `Terminated early`) — both fire this
  //: identical route with a different `event`, mirroring `sendPatch`'s own
  //: try/`ApiError`-message/`finally` shape above.
  async function confirmStatusTransition() {
    if (id === undefined || statusAction === null) {
      return;
    }
    setIsTransitioningStatus(true);
    setStatusTransitionError(undefined);
    try {
      const updated = await apiFetch<LeaseResource>(`/leases/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: statusAction }),
      });
      writeRecordToCache(updated);
      await invalidateLeaseDerivedViews(queryClient);
      setStatusAction(null);
    } catch (error) {
      setStatusTransitionError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
      //: A rejection here (e.g. `transitions.ConcurrentLeaseStatusTransitionError`
      //: — someone else already moved this lease out of `Draft` between
      //: page load and this click) means this page's own cached status may
      //: no longer match the lease's real, current one. Refetch from the
      //: server rather than leaving the dialog's background showing the
      //: stale pre-click value (VC-15.22-04) — the dialog itself stays
      //: open, showing the error, until the Owner dismisses it.
      void recordQuery.refetch();
    } finally {
      setIsTransitioningStatus(false);
    }
  }

  //: Story 11.6 (R-L8): the one real, working `GET /rent-reviews?lease_
  //: id=...` fetch this page needs — always issued once `id` is known
  //: (every role that can see the lease can see whether one is open), read-
  //: only by itself; the record-outcome form below is what gates on `isOwner`.
  const rentReviewsQueryKey = useMemo(() => ["rent-reviews", "lease", id] as const, [id]);
  const rentReviewsQuery = useQuery({
    queryKey: rentReviewsQueryKey,
    queryFn: () => apiFetch<Page<RentReviewResource>>(`/rent-reviews?lease_id=${encodeURIComponent(id ?? "")}`),
    enabled: id !== undefined,
  });
  //: At most one open review ever exists for a lease at a time (the daily
  //: evaluator's own generation sweep never creates a second one while one
  //: is open) — `find` is safe, never ambiguous.
  const openRentReview = rentReviewsQuery.data?.data.find((row) => row.reviewed_on === null) ?? null;

  const [newRentDollars, setNewRentDollars] = useState("");
  const [reviewedOnDate, setReviewedOnDate] = useState(() => todayIsoDate());
  const [isRecordingReview, setIsRecordingReview] = useState(false);
  const [recordReviewError, setRecordReviewError] = useState<string | undefined>(undefined);

  const proposedRentCents =
    newRentDollars.trim() === "" || Number.isNaN(Number(newRentDollars))
      ? null
      : Math.round(Number(newRentDollars) * 100);

  const rentControlPreview =
    openRentReview && proposedRentCents !== null
      ? previewRentControlIncrease(
          openRentReview.current_rent_cents,
          proposedRentCents,
          openRentReview.rent_control_cap_percent,
        )
      : null;

  //: `new_rent_cents`/`reviewed_on` are both required (AC: "never null and
  //: never inferred") — a "no change" outcome is expressed by the Owner
  //: simply typing the lease's own current rent back in, never a special
  //: sentinel or a second button.
  async function handleRecordRentReviewOutcome() {
    if (openRentReview === null || proposedRentCents === null) {
      return;
    }
    setIsRecordingReview(true);
    setRecordReviewError(undefined);
    try {
      const updated = await apiFetch<RentReviewResource>(`/rent-reviews/${openRentReview.id}/record`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewed_on: reviewedOnDate, new_rent_cents: proposedRentCents }),
      });
      await queryClient.invalidateQueries({ queryKey: rentReviewsQueryKey });
      setNewRentDollars("");
      showToast(
        updated.rent_control?.exceeds
          ? (updated.rent_control.message ?? "Outcome recorded — this exceeds the rent-control cap.")
          : "Rent review outcome recorded.",
      );
    } catch (error) {
      setRecordReviewError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRecordingReview(false);
    }
  }

  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | undefined>(undefined);
  const [removeTarget, setRemoveTarget] = useState<CoTenantResource | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);

  async function handleAttach() {
    if (id === undefined || selectedTenantId === "") {
      return;
    }
    setIsAttaching(true);
    setAttachError(undefined);
    try {
      const updated = await apiFetch<LeaseResource>(`/leases/${id}/tenants`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: selectedTenantId }),
      });
      writeRecordToCache(updated);
      setSelectedTenantId("");
    } catch (error) {
      setAttachError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsAttaching(false);
    }
  }

  async function confirmRemove() {
    if (id === undefined || removeTarget === null) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      const updated = await apiFetch<LeaseResource>(`/leases/${id}/tenants/${removeTarget.tenant_id}`, {
        method: "DELETE",
      });
      writeRecordToCache(updated);
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const fields = useMemo<RecordField<LeaseResource>[]>(() => {
    //: Story 15.15 — `PATCH /leases/{id}` is `role=Role.OWNER` (mirrors
    //: `PropertyRecord.tsx`'s/`TenantRecord.tsx`'s identical Tier A-
    //: established rule: `RecordPage`'s own default gate is merely
    //: `!isReadOnly`, which a Member also satisfies, and a Member is not an
    //: Owner). No archive-state gate exists for `Lease` the way `isArchived`
    //: gates `PropertyRecord.tsx`'s own fields — a lease has no analogous
    //: "locked once archived" state, so `isOwner` alone is the whole gate.
    const textEdit = isOwner ? { edit: { kind: "text" as const } } : {};
    const yesNoEdit = isOwner ? { edit: { kind: "select" as const, options: YES_NO_OPTIONS } } : {};

    const definitions: RecordField<LeaseResource>[] = [
      // -- The fields that matter — render immediately. --------------------- //
      { key: "unit_id" },
      { key: "status", render: (row) => LEASE_STATUS_LABELS[row.status] ?? row.status },
      { key: "start_date", render: (row) => formatDateCell(row.start_date), ...textEdit },
      { key: "end_date", render: (row) => (row.end_date ? formatDateCell(row.end_date) : "—"), ...textEdit },
      {
        key: "notify_by",
        render: (row) => formatNotifyBy(row.notify_by),
        provenance: (row) => notifyByProvenance(row.notify_by),
      },
      { key: "monthly_rent_cents", render: (row) => formatCents(row.monthly_rent_cents), ...textEdit },
      // -- Everything else — behind "More details". ------------------------ //
      { key: "lease_type", primary: false, render: (row) => row.lease_type ?? "—", ...textEdit },
      { key: "auto_renew", primary: false, render: (row) => formatYesNo(row.auto_renew), ...yesNoEdit },
      { key: "notice_period_days", primary: false, render: (row) => row.notice_period_days ?? "—", ...textEdit },
      { key: "holdover_terms", primary: false, render: (row) => row.holdover_terms ?? "—", ...textEdit },
      {
        key: "holdover_rent_cents",
        primary: false,
        render: (row) => formatCents(row.holdover_rent_cents),
        ...textEdit,
      },
      { key: "rent_due_day", primary: false, render: (row) => row.rent_due_day ?? "—", ...textEdit },
      { key: "grace_period_days", primary: false, render: (row) => row.grace_period_days ?? "—", ...textEdit },
      { key: "late_fee_cents", primary: false, render: (row) => formatCents(row.late_fee_cents), ...textEdit },
      {
        key: "returned_check_fee_cents",
        primary: false,
        render: (row) => formatCents(row.returned_check_fee_cents),
        ...textEdit,
      },
      { key: "pet_rent_cents", primary: false, render: (row) => formatCents(row.pet_rent_cents), ...textEdit },
      {
        key: "parking_fee_cents",
        primary: false,
        render: (row) => formatCents(row.parking_fee_cents),
        ...textEdit,
      },
      {
        key: "storage_fee_cents",
        primary: false,
        render: (row) => formatCents(row.storage_fee_cents),
        ...textEdit,
      },
      // Structured list-shaped fields stay read-only this story (this
      // page's own top-of-file doc comment: mirrors `TenantRecord.tsx`'s
      // identical `additional_occupants`/`pets`/`vehicles` precedent — no
      // `...textEdit` spread on any of these three).
      { key: "utilities_included", primary: false, render: (row) => formatStringList(row.utilities_included) },
      {
        key: "scheduled_escalation",
        primary: false,
        render: (row) => formatScheduledEscalation(row.scheduled_escalation),
      },
      {
        key: "deposit_amount_cents",
        primary: false,
        render: (row) => formatCents(row.deposit_amount_cents),
        ...textEdit,
      },
      {
        key: "deposit_date_received",
        primary: false,
        render: (row) => (row.deposit_date_received ? formatDateCell(row.deposit_date_received) : "—"),
        ...textEdit,
      },
      { key: "deposit_bank_name", primary: false, render: (row) => row.deposit_bank_name ?? "—", ...textEdit },
      {
        key: "deposit_account_number",
        primary: false,
        render: (row) => row.deposit_account_number ?? "—",
        ...textEdit,
      },
      {
        key: "deposit_interest_bearing",
        primary: false,
        render: (row) => formatYesNo(row.deposit_interest_bearing),
        ...yesNoEdit,
      },
      {
        key: "deposit_last_interest_paid_date",
        primary: false,
        render: (row) =>
          row.deposit_last_interest_paid_date ? formatDateCell(row.deposit_last_interest_paid_date) : "—",
        ...textEdit,
      },
      {
        key: "deposit_annual_interest_amount_cents",
        primary: false,
        render: (row) => formatCents(row.deposit_annual_interest_amount_cents),
        ...textEdit,
      },
      {
        key: "deposit_disposition",
        primary: false,
        render: (row) => row.deposit_disposition ?? "—",
        ...textEdit,
      },
      {
        key: "deposit_location_notice_date",
        primary: false,
        render: (row) => (row.deposit_location_notice_date ? formatDateCell(row.deposit_location_notice_date) : "—"),
        ...textEdit,
      },
      {
        key: "truth_in_renting_provided_date",
        primary: false,
        render: (row) =>
          row.truth_in_renting_provided_date ? formatDateCell(row.truth_in_renting_provided_date) : "—",
        ...textEdit,
      },
      {
        key: "lead_paint_disclosure_provided_date",
        primary: false,
        render: (row) =>
          row.lead_paint_disclosure_provided_date ? formatDateCell(row.lead_paint_disclosure_provided_date) : "—",
        ...textEdit,
      },
      {
        key: "registration_statement_copy_provided_date",
        primary: false,
        render: (row) =>
          row.registration_statement_copy_provided_date
            ? formatDateCell(row.registration_statement_copy_provided_date)
            : "—",
        ...textEdit,
      },
      // Read-only — see this page's own top-of-file doc comment.
      { key: "lease_documents", primary: false, render: (row) => formatStringList(row.lease_documents) },
      { key: "renewed_into_lease_id", primary: false, render: (row) => row.renewed_into_lease_id ?? "—" },
    ];
    return definitions;
  }, [isOwner]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;

  const coTenants = recordQuery.data?.tenants ?? [];
  const attachedTenantIds = new Set(coTenants.map((row) => row.tenant_id));
  const availableTenants = (tenantsQuery.data?.data ?? []).filter((row) => !attachedTenantIds.has(row.id));
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      <RecordPage<LeaseResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => `Lease starting ${formatDateCell(row.start_date)}`}
        backLink={{ label: "Leases", href: "/tenants-leases/leases" }}
        icon={DocumentsIcon}
        tint="cyan"
        onCommitEdit={commitEdit}
      />
      {/*
        Story 15.22 — `Draft`'s own two legal exits (`transitions.py`'s
        `_TRANSITIONS` table), gated on `isOwner` mirroring `PATCH /leases/
        {id}/status`'s own `role=Role.OWNER` and this page's identical gate
        on "Start a renewal"/"Remove"/"Add" just below. Neither renders once
        the lease is no longer `Draft` (VC-15.22-01) — no client-side
        precondition beyond that is added (Judgement call 2: a zero-tenant
        Draft lease is not blocked here, exactly as the backend already
        allows).
      */}
      {isOwner && recordQuery.data && recordQuery.data.status === "draft" ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <Button type="button" variant="secondary" onClick={() => setStatusAction("sign_and_start")}>
            Sign & start
          </Button>
          <Button type="button" variant="secondary" onClick={() => setStatusAction("cancel")}>
            Cancel lease
          </Button>
        </div>
      ) : null}
      {isOwner && recordQuery.data ? (
        <div className="mt-4">
          <Button
            type="button"
            variant="secondary"
            disabled={isStartingRenewal}
            onClick={() => void handleStartRenewal(recordQuery.data!.id)}
          >
            Start a renewal
          </Button>
        </div>
      ) : null}
      {/*
        Story 11.6 (R-L8): shown only when an open RentReview exists for
        this lease (AC's own "no empty rent-review section cluttering every
        other lease's record page") — visible read-only to every role that
        can already see the lease; the recording form itself is Owner-only.
      */}
      {recordQuery.data && openRentReview ? (
        <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
          <h2 className="text-heading font-semibold text-ink">Rent review due</h2>
          <p className="mt-2 text-body text-ink">
            Due {formatDateCell(openRentReview.cycle)} — current rent {formatCents(openRentReview.current_rent_cents)}
          </p>
          {isOwner ? (
            <form
              className="mt-4 flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                void handleRecordRentReviewOutcome();
              }}
            >
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">New monthly rent</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={newRentDollars}
                  onChange={(event) => setNewRentDollars(event.target.value)}
                  placeholder="0.00"
                  className="h-tap-target-min w-40 rounded-md border border-border-input bg-surface px-3 text-body text-ink focus:border-border-focus"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Reviewed on</span>
                <input
                  type="date"
                  value={reviewedOnDate}
                  onChange={(event) => setReviewedOnDate(event.target.value)}
                  className="h-tap-target-min rounded-md border border-border-input bg-surface px-3 text-body text-ink focus:border-border-focus"
                />
              </label>
              <Button type="submit" disabled={proposedRentCents === null || isRecordingReview}>
                {isRecordingReview ? "Recording…" : "Record outcome"}
              </Button>
            </form>
          ) : null}
          {rentControlPreview?.exceeds ? (
            <p role="alert" className="mt-3 text-meta text-danger">
              This is a {rentControlPreview.increasePercent.toFixed(2)}% increase, which exceeds the{" "}
              {rentControlPreview.capPercent.toFixed(2)}% rent-control cap.
            </p>
          ) : null}
          {recordReviewError ? (
            <p role="alert" className="mt-2 text-meta text-danger">
              {recordReviewError}
            </p>
          ) : null}
        </section>
      ) : null}
      {/*
        VC-6.5-07: each co-tenant listed inline with real, working
        tel:/sms:/mailto: actions — built via `ContactQuickLinks`, the same
        shared component `TenantRecord.tsx` renders (Story 6.5's own
        "second real call site, extract now" judgement call). Not built on
        `RelatedRecordsSection` (mirrors `TenantRecord.tsx`'s own
        communication-log precedent): that component wraps its whole row in
        a navigate-away `onClick`, which would fight with a real `<a
        href="tel:...">` inside the same row for the click.
      */}
      {recordQuery.data ? (
        <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
          <h2 className="text-heading font-semibold text-ink">Co-tenants</h2>
          {coTenants.length === 0 ? (
            <EmptyState
              className="mt-2"
              heading="No co-tenants yet."
              body="Once a tenant is attached to this lease, they will appear here with their own call, text and email actions."
            />
          ) : (
            <ul className="mt-4 flex flex-col gap-3">
              {coTenants.map((coTenant) => (
                <li
                  key={coTenant.tenant_id}
                  className="rounded-lg border border-border-hairline bg-surface p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link
                      to={`/tenants-leases/${coTenant.tenant_id}`}
                      className="text-body font-medium text-ink underline"
                    >
                      {coTenant.full_name}
                    </Link>
                    {isOwner ? (
                      <Button
                        variant="ghost"
                        aria-label={`Remove ${coTenant.full_name}`}
                        onClick={() => setRemoveTarget(coTenant)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                  <ContactQuickLinks
                    mobilePhone={coTenant.mobile_phone}
                    email={coTenant.email}
                    className="mt-3"
                  />
                </li>
              ))}
            </ul>
          )}
          {isOwner ? (
            <form
              className="mt-4 flex flex-wrap items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void handleAttach();
              }}
            >
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Add a co-tenant</span>
                <select
                  value={selectedTenantId}
                  onChange={(event) => setSelectedTenantId(event.target.value)}
                  className="h-tap-target-min min-w-[16rem] rounded-md border border-border-input bg-surface px-3 text-body text-ink focus:border-border-focus"
                >
                  <option value="">Choose a tenant…</option>
                  {availableTenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.full_name}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit" disabled={selectedTenantId === "" || isAttaching}>
                {isAttaching ? "Adding…" : "Add"}
              </Button>
            </form>
          ) : null}
          {attachError ? (
            <p role="alert" className="mt-2 text-meta text-danger">
              {attachError}
            </p>
          ) : null}
        </section>
      ) : null}
      {removeTarget ? (
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveTarget(null);
              setRemoveError(undefined);
            }
          }}
          title="Remove this co-tenant?"
          description={`Removing ${removeTarget.full_name} takes them off this lease right away. Their own tenant record and history are untouched, and they can be attached again later.`}
          confirmLabel={`Remove ${removeTarget.full_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={contentRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
      {/*
        Story 15.22 — a lightweight confirm step before either one-way
        `Draft` exit fires, mirroring `ConfirmDialog`'s established pattern
        above (`removeTarget`) and on `PropertyRecord.tsx`'s own `Archive`
        action (spec judgement call 1). `confirmLabel` deliberately reads
        differently from the trigger button's own label ("Sign & start
        this lease" vs. the trigger's "Sign & start") so the two buttons
        never collide once the dialog is open — the trigger stays mounted
        underneath until the transition actually succeeds.
      */}
      {statusAction === "sign_and_start" ? (
        <ConfirmDialog
          open={statusAction === "sign_and_start"}
          onOpenChange={(open) => {
            if (!open) {
              setStatusAction(null);
              setStatusTransitionError(undefined);
            }
          }}
          title="Sign & start this lease?"
          description="This marks the lease Active, as if it was just signed. Its start and end dates then become the ones shown on its tenant's and property's own records. This cannot be undone from here."
          confirmLabel="Sign & start this lease"
          confirmingLabel="Starting…"
          isConfirming={isTransitioningStatus}
          onConfirm={() => void confirmStatusTransition()}
          fallbackFocusRef={contentRef}
          {...(statusTransitionError ? { errorMessage: statusTransitionError } : {})}
        />
      ) : null}
      {statusAction === "cancel" ? (
        <ConfirmDialog
          open={statusAction === "cancel"}
          onOpenChange={(open) => {
            if (!open) {
              setStatusAction(null);
              setStatusTransitionError(undefined);
            }
          }}
          title="Cancel this lease?"
          description="This moves the lease to Terminated early — it never signed, and it will not become this property's or tenant's active lease. This cannot be undone from here."
          confirmLabel="Cancel this lease"
          confirmingLabel="Cancelling…"
          isConfirming={isTransitioningStatus}
          onConfirm={() => void confirmStatusTransition()}
          fallbackFocusRef={contentRef}
          {...(statusTransitionError ? { errorMessage: statusTransitionError } : {})}
        />
      ) : null}
    </div>
  );
}
